import React from 'react';

import { LockFilled } from '@ant-design/icons';
import { Switch } from 'antd';
import { isMobileOnly } from 'react-device-detect';

import TableRowButtons from '@/components/Common/TableRowButtons';
import { createTableActionsColumn } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import { registerModuleFilters } from '@/utils/input/filters';
import { buildAuditFilters } from '@/utils/input/filters/auditFilters';
import BaseFilters from '@/utils/input/filters/BaseFilters';

import { MODEL_NAME } from './constants';

const PowerBIServerConfigurationFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'powerBIServerConfigurationId',
  label: 'reportGroups.form.powerBIServerConfigurationId',
  apiRoute: 'powerBIServerConfiguration/allowed',
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const auditFilters = buildAuditFilters();

export const filtersConfig = [PowerBIServerConfigurationFilter, ...auditFilters];

/** Used by reports launchpad / list filters — keep export stable. */
export const groupsFiltersConfig = filtersConfig;

registerModuleFilters(MODEL_NAME, filtersConfig);

export function buildReportGroupColumns({
  formatMessage,
  searchCriteria,
  openDrawerCreateOrEdit,
  deleteItemConfirm,
  activate,
  activatingItemIds,
  list,
  openPermissions,
  isMobileView = isMobileOnly,
}) {
  const forceDropdown = list?.some((item) => item.isActive) ?? false;
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'isActive',
      title: formatMessage({ id: 'global.text.enabled' }),
      dataIndex: 'isActive',
      align: 'center',
      width: 120,
      sorter: true,
      render: (isActive, item) => (
        <Switch
          checked={isActive}
          loading={!!activatingItemIds?.[item?.id]}
          onChange={(active) => activate(item?.id, active)}
        />
      ),
    },
    {
      key: 'order',
      title: formatMessage({ id: 'global.order' }),
      dataIndex: 'order',
      sorter: true,
      width: 100,
      render: (value, record) => (record.order !== undefined ? record.order : 0),
    },
    {
      key: 'name',
      title: formatMessage({ id: 'global.name' }),
      dataIndex: 'name',
      sorter: true,
      render: highlight,
    },
    {
      key: 'powerBIServerConfiguration/name',
      title: formatMessage({ id: 'reportGroups.form.powerBIServerConfigurationId' }),
      dataIndex: ['powerBIServerConfiguration', 'name'],
      sorter: true,
      render: (_, record) =>
        highlight(record.powerBIServerConfiguration?.name ?? record.powerBIServerConfigurationId),
    },
    {
      key: 'powerBIReportGroupId',
      title: formatMessage({ id: 'reportGroups.form.powerBIReportGroupId' }),
      dataIndex: 'powerBIReportGroupId',
      sorter: true,
      render: highlight,
    },
    createTableActionsColumn({
      alwaysDropdown: forceDropdown,
      render: (_, record) => {
        const canShowPermissions = record.isActive;
        const customItems = canShowPermissions
          ? [
              {
                key: 'permissions',
                label: formatMessage({ id: 'global.text.permissions' }),
                icon: <LockFilled />,
                onClick: (e) => {
                  e?.preventDefault?.();
                  e?.stopPropagation?.();
                  openPermissions?.(record);
                },
              },
              { key: 'divider-1', type: 'divider' },
            ]
          : [];

        return (
          <TableRowButtons
            onDetail={() => openDrawerCreateOrEdit(record.id, true, null)}
            onEdit={() => openDrawerCreateOrEdit(record.id, false, null)}
            onRemove={() => deleteItemConfirm(record.id)}
            formatMessage={formatMessage}
            isMobileView={isMobileView}
            customItems={customItems}
            actionOrder={{
              primary: [],
              secondary: canShowPermissions
                ? ['permissions', 'divider-1', 'detail', 'edit', 'delete']
                : ['detail', 'edit', 'delete'],
            }}
            forceDropdown={forceDropdown}
          />
        );
      },
    }),
  ];
}
